/**
 * 通用重试工具函数
 *
 * 支持指数退避、可中断和可配置的重试策略
 */

export interface RetryOptions {
  /** 最大重试次数（默认 3） */
  maxRetries?: number;
  /** 初始延迟毫秒数（默认 1000） */
  initialDelayMs?: number;
  /** 退避倍数（默认 2） */
  backoffFactor?: number;
  /** 最大延迟毫秒数（默认 10000） */
  maxDelayMs?: number;
  /** 可重试的错误判断函数 */
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  /** 重试前的回调 */
  onRetry?: (error: unknown, attempt: number, delayMs: number) => void;
  /** AbortSignal 用于取消重试 */
  signal?: AbortSignal;
}

/** 默认可重试错误判断：网络错误、超时、5xx 错误 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    // 网络错误
    if (msg.includes('network') || msg.includes('fetch') || msg.includes('timeout')) {
      return true;
    }
    // 超时
    if (error.name === 'AbortError' || msg.includes('abort')) {
      return true;
    }
  }
  // HTTP 5xx 或 429 错误
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status: number }).status;
    return status >= 500 || status === 429;
  }
  return false;
}

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Retry cancelled'));
      return;
    }
    const timeoutId = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timeoutId);
      reject(new Error('Retry cancelled'));
    });
  });

/**
 * 带重试的异步函数执行器
 *
 * @param fn 要执行的异步函数
 * @param options 重试配置
 * @returns Promise 结果
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelayMs = 1000,
    backoffFactor = 2,
    maxDelayMs = 10000,
    shouldRetry = isRetryableError,
    onRetry,
    signal,
  } = options;

  let lastError: unknown;
  let delayMs = initialDelayMs;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) {
      throw new Error('Retry cancelled');
    }

    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // 最后一次尝试或不可重试的错误，直接抛出
      if (attempt >= maxRetries || !shouldRetry(error, attempt)) {
        throw error;
      }

      // 计算下次延迟
      const actualDelay = Math.min(delayMs, maxDelayMs);
      onRetry?.(error, attempt + 1, actualDelay);

      // 等待后重试
      await sleep(actualDelay, signal);
      delayMs *= backoffFactor;
    }
  }

  throw lastError;
}
